/*
 * This module is for development purposes only! Once the back-office API is ready,
 * all requests should be handled via Axios directly.
 */

import axios, { AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios';
import AxiosMockAdapter from 'axios-mock-adapter';
import { get } from 'lodash';
// TODO: remove AxiosMockAdapter once we have a real API
import { handleDates } from '@/api/handle-dates';
import logger from '@/utils/logger.mjs';
import { makeUseAxios } from 'axios-hooks';
import AppConfig from '@/utils/app-config.mjs';
import Transfer from '@/model/transfer.mjs';
import { SERVER_PAGE_SIZE } from '@/model/Constants';
import { createCompareFn, SortType } from '@/api/utils.mjs';
import fflGetDealersByZip from '../../cypress/fixtures/ffl-get/geo-78731-05.json';
import fflGetDealerById from '../../cypress/fixtures/ffl-get/id-sample-01.json';
import LicenseeBuilder from "@/model/builders/ffl-get-transformer.mjs";
import { fetchCognitoToken } from '@/utils/authentication'
import TransferBuilder from '@/model/builders/ffl-boa-get-transformer.mjs';
import LicenseDetailBuilder from '@/model/builders/ffl-get-license-details.mjs';

export const API_KEYWORD_SEPARATOR = '::';

// const baseUrl = AppConfig.backOfficeApiBaseURL;
const baseURL = AppConfig.fflGetApiBaseURL;

export const client = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    accept: 'application/json',
  },
  withCredentials: true,
});

/**
 * Setting authorization
 */
client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await fetchCognitoToken();
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${ token}`
  } as AxiosRequestHeaders
  return config
}, error => {
  return Promise.reject(error)
})

/**
 * A hook that uses Axios on the back-end.
 */
export const useClient = makeUseAxios({
  axios: client,
  defaultOptions: {
    useCache: false
  }
});

export const useFFLDetailClient = makeUseAxios({
  axios: client,
  defaultOptions: {
    useCache: false
  }
});

/**
 * Automatically convert ISO dates in the response to Date objects
 */
client.interceptors.response.use((originalResponse) => {
  logger.debug(
    'intercepted',
    'constructor',
    originalResponse.constructor.name,
    'originalResponse',
    originalResponse
  );
  /**
   * If the response is an FFLGet dealer list, transform this to our internal model
   */
  // console.log("originalResponse", originalResponse)
  if (originalResponse.data.dealers && originalResponse.data.meta) {
    logger.debug('**** got a dealer list');
    const converted = LicenseeBuilder.buildLicenseeList(originalResponse.data);
    logger.debug('converted to', converted);
    originalResponse.data = converted;
  } else if (originalResponse.data.dealers){
    logger.debug('**** got a dealer data');
    const converted = LicenseeBuilder.buildLicenseDetails(originalResponse.data);
    logger.debug('converted to', converted);
    originalResponse.data = converted;
  } else if (originalResponse.data.licensename) {
    logger.debug('**** got a dealer record');
    const converted = LicenseeBuilder.buildLicensee(originalResponse.data);
    logger.debug('converted to', converted);
    originalResponse.data = converted;
  } else if (originalResponse.request.responseURL.includes('/transfers') && originalResponse.data.items) {
    logger.debug('**** got a transfer list');
    const converted = TransferBuilder.buildTransferList(originalResponse.data);
    originalResponse.data.items = converted;
    logger.debug('converted to', converted);
  } else if (originalResponse?.data?.data?.length > 0){
    logger.debug('**** got a record for seller/reciever details',originalResponse);
    const converted = LicenseDetailBuilder.buildLicenseDetailsForTransfer(originalResponse?.data?.data[0]);
    originalResponse.data.items = converted;
    logger.debug('converted to', converted);
  }
  // else {
  //   logger.debug('**** no this is not an ffl-get response', originalResponse.data);
  //   handleDates(originalResponse.data);
  // }
  return originalResponse;
});

/*
  Passthrough all requests that aren't mocked
 */
export const mock = new AxiosMockAdapter(client, {
  delayResponse: 100,
  onNoMatch: 'passthrough'
});

if (AppConfig.fflGetMockAPI) {
  const mockFFLGeoURL = new RegExp(
    `${AppConfig.fflGetApiBaseURL}/geo/\\d+/\\d+`
  );
  mock.onGet(mockFFLGeoURL).reply((config) => {
    logger.debug('** Mocked ** /geo', config.url);
    return [200, fflGetDealersByZip];
  });

  const mockFFLByIDURL = new RegExp(
    `${AppConfig.fflGetApiBaseURL}/license/\\d{3}/\\d{5}`
  );
  mock.onGet(mockFFLByIDURL).reply((config) => {
    logger.debug('** Mocked ** /license', config.url);
    return [200, fflGetDealerById];
  });
}

const transferQueue: Transfer[] = [];

export interface PaginatedResult<T> {
  data: T[];
  pageSize: number;
  startRow: number;
  totalRows: number;
}

/**
 * Get a list of transfers, filtered, sorted, and paginated
 */
mock.onGet(`${baseURL}/transfers`).reply((config) => {
  const startRow = config.params?.startRow || 0;
  const pageSize = config.params?.pageSize || SERVER_PAGE_SIZE;

  // an array of "field::direction"
  const orderBy = config.params?.orderBy;

  /*
    An array of "field::filter-type::value"
    - `field `may be nested, e.g. `licensee.name`
    - filter-type: "equals" | "contains" | "notContains" | "inSet" | "greaterThan" | "lessThan" | "notEqual" | "inRange" | "blank" | "notBlank"
    */
  const filters: string[] = config.params?.filter;

  // use mock values here
  let queue = [...transferQueue];
  if (filters?.length) {
    filters.forEach((filter) => {
      const [field, op, value] = filter.split(API_KEYWORD_SEPARATOR);
      logger.debug('filter', 'field:', field, 'op:', op, 'value:', value);
      switch (op) {
        case 'blank':
        case 'notBlank': {
          queue = queue.filter((transfer) => {
            const ref: string = (get(transfer, field) || '')
              .toString()
              .toLowerCase();
            const matches = ref === null || ref === undefined || ref === '';
            return op === 'blank' ? matches : !matches;
          });
          break;
        }
        case 'contains':
        case 'notContains': {
          queue = queue.filter((transfer) => {
            const ref: string = (get(transfer, field) || '')
              .toString()
              .toLowerCase();
            const matches = ref.includes(value.toLowerCase());
            return op === 'contains' ? matches : !matches;
          });
          break;
        }
        case 'equals': {
          queue = queue.filter(
            (transfer) => value === get(transfer, field).toString()
          );
          break;
        }
        case 'inSet': {
          if (value.length) {
            // filter for any transfer with a field that matches one of the set values
            const values = value.split(';');
            queue = queue.filter((transfer) => {
              return values.includes(get(transfer, field).toString());
            });
          }
          break;
        }
        case 'startsWith': {
          queue = queue.filter((transfer) => {
            const ref: string = (get(transfer, field) || '')
              .toString()
              .toLowerCase();
            return ref.startsWith(value.toLowerCase());
          });
          break;
        }
        case 'endsWith': {
          queue = queue.filter((transfer) => {
            const ref: string = (get(transfer, field) || '')
              .toString()
              .toLowerCase();
            return ref.endsWith(value.toLowerCase());
          });
          break;
        }

        default: {
          logger.error('No handler for op', op);
        }
      }
    });
  }

  if (queue && orderBy?.length) {
    // Sort only on the first sort term because this is a demo. The production
    // API will need to respect multiple sort terms

    type ObjectKey = keyof Transfer;
    const [field, direction]: [field: ObjectKey, direction: SortType] =
      config.params.orderBy[0].split(API_KEYWORD_SEPARATOR);
    queue.sort(createCompareFn<Transfer>(field, direction));
  }

  const data = queue.slice(startRow, startRow + pageSize);

  return [
    200,
    {
      data,
      totalRows: data.length,
      startRow,
      pageSize
    } as PaginatedResult<Transfer>
  ];
});

/**
 * Return a list of distinct agent names
 */
mock.onGet(`${baseURL}/agents/`).reply(() => {
  const agents = new Set(transferQueue.map((val) => val.agent));
  return [
    200,
    {
      data: [...agents].sort()
    }
  ];
});